/**
 * @file index.ts
 * @module types/roles_permission/index
 * @description Defines TypeScript contracts used to represent roles, permissions, modules, and access-control assignments.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { PermissionModule } from '../../utils/constants';

export const PermissionAndModuleName: Record<
  PermissionModule,
  { moduleName: string }
> = {
  [PermissionModule.AllModules]: { moduleName: 'AllModules' },
  [PermissionModule.Sellers]: { moduleName: 'Sellers' },
  [PermissionModule.AllSeller]: { moduleName: 'All Sellers' },
  [PermissionModule.KycVerifications]: { moduleName: 'KYC Verification' },
  [PermissionModule.PendingCertificate]: { moduleName: 'Pending Certificate' },
  [PermissionModule.PaymentToSellers]: { moduleName: 'Payment to Sellers' },
  [PermissionModule.SellersSalesReport]: { moduleName: 'Sales Report' },
  [PermissionModule.Customers]: { moduleName: 'Customers' },
  [PermissionModule.AllCustomers]: { moduleName: 'All Customers' },
  [PermissionModule.RefundsToCustomer]: { moduleName: 'Refunds to Customers' },
  [PermissionModule.ReceivableReports]: { moduleName: 'Receivable Reports' },
  [PermissionModule.OrdersDelivery]: { moduleName: 'Orders & delivery' },
  [PermissionModule.RoutePlan]: { moduleName: 'Route Planing' },
  [PermissionModule.Master]: { moduleName: 'Master' },
  [PermissionModule.Categories]: { moduleName: 'Categories' },
  [PermissionModule.BusinessType]: { moduleName: 'Business types' },
  [PermissionModule.BusinessCertificates]: {
    moduleName: 'Business certificates',
  },
  [PermissionModule.Brand]: { moduleName: 'Brands' },
  [PermissionModule.TechnicalDetails]: { moduleName: 'Technical details' },
  [PermissionModule.ProductVariants]: { moduleName: 'Product variants' },
  [PermissionModule.MasterProduct]: { moduleName: 'Master Products' },
  [PermissionModule.Driver]: { moduleName: 'Drivers' },
  [PermissionModule.Vehicle]: { moduleName: 'Vehicles' },
  [PermissionModule.VehicleLoadingArea]: {
    moduleName: 'Vehicle loading areas',
  },
  [PermissionModule.SellerProductZone]: { moduleName: 'Seller product zone' },
  [PermissionModule.FinanceMember]: { moduleName: 'Finance members' },
  [PermissionModule.RolesAndPermission]: { moduleName: 'Roles & Permissions' },
  [PermissionModule.UserManagement]: { moduleName: 'User Management' },
  [PermissionModule.Inventory]: { moduleName: 'Inventory' },
  [PermissionModule.GoodsWarehouse]: { moduleName: 'Goods in Warehouse' },
  [PermissionModule.LowStockLists]: { moduleName: 'Low stock list' },
  [PermissionModule.GoodsRequest]: { moduleName: 'Goods requests' },
  [PermissionModule.ReceivedDirectGood]: {
    moduleName: 'Received direct goods',
  },
  [PermissionModule.CustomerServices]: { moduleName: 'Customer service' },
  [PermissionModule.All_Products]: { moduleName: 'All Products' },
  [PermissionModule.PlatformFees]: { moduleName: 'Platform fees' },
  [PermissionModule.Advertising]: { moduleName: 'Advertising' },
  [PermissionModule.ContactInquires]: { moduleName: 'Contact inquiries' },
  [PermissionModule.CustomNotifications]: { moduleName: 'Custom Notification' },
  [PermissionModule.Settings]: { moduleName: 'General settings' },
  [PermissionModule.Privacy_Policy]: { moduleName: 'Privacy Policy' },
  [PermissionModule.TermsConditions]: { moduleName: 'Terms & Conditions' },
  [PermissionModule.AppSettings]: { moduleName: 'App Settings' },
  [PermissionModule.Dashboard]: { moduleName: 'Dashboard' },
  [PermissionModule.FinanceReports]: { moduleName: 'Finance Reports' },
  [PermissionModule.OutWardReports]: { moduleName: 'Outward Reports' },
  [PermissionModule.ReturnProductReports]: {
    moduleName: 'Return Product Reports',
  },
  [PermissionModule.DeliveryReports]: { moduleName: 'Delivery Reports' },
  [PermissionModule.Reports]: { moduleName: 'Reports' },
  [PermissionModule.CustomerPurchaseBehaviourReport]: {
    moduleName: 'Customer Purchase Behaviour Report',
  },
  [PermissionModule.GeographicSalesInsightReport]: {
    moduleName: 'Geographic Sales Insight Report',
  },
  [PermissionModule.AreaBasedCustomerSegmentationReport]: {
    moduleName: 'Area Based Customer Segmentation Report',
  },
  [PermissionModule.FrequentCustomerPurchasePatternAnalysisReport]: {
    moduleName: 'Frequent Customer Purchase Pattern Analysis Report',
  },
  [PermissionModule.ProductSalesPerformanceReport]: {
    moduleName: 'Product Sales Performance Report',
  },
  [PermissionModule.BrandWiseSalesPerformanceReport]: {
    moduleName: 'Brand Wise Sales Performance Report',
  },
  [PermissionModule.CategoryAndSubCategoryAnalysisReport]: {
    moduleName: 'Category And SubCategory AnalysisReport',
  },
  [PermissionModule.RevenueGenerationReport]: {
    moduleName: 'Revenue Generation Report',
  },
  [PermissionModule.CommissionAnalysisReport]: {
    moduleName: 'Commission Analysis Report',
  },
  [PermissionModule.InventoryStatusReport]: {
    moduleName: 'Inventory Status Report',
  },
  [PermissionModule.InterTransferUmart]: {
    moduleName: 'Inter Transfer Request (UMart)',
  },
  [PermissionModule.PreOrderCustomerList]: { moduleName: 'Pre Order Customer' },
  [PermissionModule.WarehouseModule]: { moduleName: 'Warehouse' },
  [PermissionModule.WarehouseManger]: { moduleName: 'Warehouse' },
  [PermissionModule.WarehouseInterTransfer]: {
    moduleName: 'Inter Transfer Request',
  },
  [PermissionModule.PendingDeliveryOrder]: {
    moduleName: 'Pending Delivery Orders',
  },
  [PermissionModule.OutOfStockList]: { moduleName: 'Out Of Stock' },
  [PermissionModule.OutstandingReport]: { moduleName: 'Outstanding Report' },
  [PermissionModule.DriverLedger]: { moduleName: 'Driver Ledger' },
  [PermissionModule.DistrictAndCity]: { moduleName: 'District & City' },
  [PermissionModule.CustomerLedger]: { moduleName: 'Customer Ledger' },
  [PermissionModule.PriceUpdateRequest]: {
    moduleName: 'Price Update Request (Approval)',
  },
  [PermissionModule.RouteProductReports]: {
    moduleName: 'Route Product Reports',
  },
  [PermissionModule.POManagement]: { moduleName: 'PO Management' },
  [PermissionModule.AllVendors]: { moduleName: 'All Vendors' },
  [PermissionModule.PaymentToVendors]: { moduleName: 'Payment To Vendors' },
  [PermissionModule.AssignProductToVendors]: {
    moduleName: 'Assign Product To Vendors',
  },
  [PermissionModule.GoodsReceivedNote]: { moduleName: 'Goods Received Note' },
  [PermissionModule.Escalations]: { moduleName: 'Escalations' },
  [PermissionModule.VendorLedger]: { moduleName: 'Vendor Ledger' },
  [PermissionModule.InventoryReplenishmentReport]: {
    moduleName: 'Inventory Replenishment Report',
  },
  [PermissionModule.SalesDashboard]: { moduleName: 'Sales Dashboard' },
  [PermissionModule.SalesRepresentative]: {
    moduleName: 'Sales Representative',
  },
  [PermissionModule.CustomerAssignment]: { moduleName: 'Customer Assignment' },
  [PermissionModule.TargetManagement]: { moduleName: 'Target Management' },
  [PermissionModule.SalesRepOrders]: { moduleName: 'Sales Rep Orders' },
  [PermissionModule.SalesAlerts]: { moduleName: 'Sales Alerts' },
  [PermissionModule.FinanceSalesReportPermission]: {
    moduleName: 'Finance Sales Report',
  },
  [PermissionModule.FinanceReturnReportPermission]: {
    moduleName: 'Finance Return Report',
  },
  [PermissionModule.FinanceDeliveryReportPermission]: {
    moduleName: 'Finance Delivery Report',
  },
  [PermissionModule.ProductReturnRequests]: { moduleName: 'Return Requests' },
  [PermissionModule.UProcure]: { moduleName: 'UProcure' },
  [PermissionModule.UProcureDashboard]: { moduleName: 'UProcureDashboard' },
  [PermissionModule.InventoryReportPermission]: {
    moduleName: 'Inventory Report',
  },
  [PermissionModule.purchaseOrder]: { moduleName: 'Purchase Order' },
  [PermissionModule.FinanceOrderReportPermission]: {
    moduleName: 'Finance Order Report',
  },
  [PermissionModule.ActivityLogs]: { moduleName: 'Activity Logs' },
  [PermissionModule.PriceCatalog]: { moduleName: 'Price Catalog' },
  [PermissionModule.DispatchReport]: {
    moduleName: 'Dispatch Report'
  },
  [PermissionModule.DispatchReportOnlyReturnConsidered]: {
    moduleName: 'Dispatch Report(Only Return Considered)'
  },
  [PermissionModule.DispatchReportWithReturnedCombined]: {
    moduleName: 'Dispatch Report With Returned Combined'
  },
  [PermissionModule.Inward]: { moduleName: 'Inward' },
};

export const rolesPermissions: Record<number, string> = {
  0: 'All',
  1: 'View',
  2: 'Add',
  3: 'Edit',
  4: 'Delete',
  5: 'Download',
  6: 'Dispatch',
  17: 'Edit Order',
  18: 'Load Bypass',
  19: 'Address Edit',
  20: 'Order Cancel',
  29: 'Restrict order view',
  30: 'View Route',
  31: 'Cancel Delivery Plan',
  32: 'Download GR Document',
  33: 'Download Order Document',
  34: 'Utrade Discount',
  // Inward (70-77)
  70: 'Create GRN (old)',
  71: 'Loading Bay Manager',
  72: 'Warehouse Approval (verify with photo)',
  73: 'Finance Approval (upload documents)',
  74: 'Assign Loading Bay Manager & Dock',
  75: 'Procurement: Acknowledge Cost Price & Update Selling Price',
  76: 'Verify EFD receipt',
  77: 'Reject GRN',
};

/** Display order for permission list*/
export const permissionDisplayOrder: number[] = [
  0, // All
  1, // View / List
  2, // Add
  3, // Edit
  4, // Delete
  5, // Download
  6, // Dispatch
  18, // Load Bypass
  17, // Edit Order
  19, // Address Edit
  20, // Order Cancel
  34, // Utrade Discount
  29, // Restrict order view
  30, // View Route
  31, // Cancel Delivery Plan
  32, // Download GR Document
  33, // Download Order Document
  71, // Loading Bay Manager (Inward)
  74, // Assign Loading Bay Manager & Dock (Inward)
  72, // Warehouse Approval (Inward)
  73, // Finance Approval (Inward)
  75, // Acknowledge Cost Price Change (Inward)
  76, // Verify EFD receipt (Inward report)
  77, // Reject GRN (Inward)
  70, // Create GRN (Inward, old)
];
